/**
 * "WHAT THIS MEANS" at the top of a table: a short paragraph about what the
 * table's numbers mean for the room's question. The same paragraph shows on
 * the shared report, so the two never disagree.
 *
 * It is written again as the last thing each run does, for the tables whose
 * numbers the run moved. In between, the numbers can move (a dig, or a person
 * sorting the board), so the paragraph says when it no longer matches them and
 * can be written again then, without waiting for a run that may not come.
 */
import { useEffect, useState } from 'react';
import type { BoardroomTableSummary } from '../../../types';

/** "just now", "5 minutes ago", "3 hours ago", "2 days ago". */
export function writtenAgo(iso: string | null, now: number = Date.now()): string {
  if (!iso) return '';
  const mins = Math.max(0, Math.round((now - new Date(iso).getTime()) / 60000));
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} minute${mins === 1 ? '' : 's'} ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  const days = Math.round(hours / 24);
  return `${days} day${days === 1 ? '' : 's'} ago`;
}

/** One line of "What this means": its label, and what it says. */
export interface MeaningLine {
  label: string | null;
  text: string;
}

const MEANING_LABELS = ['What it shows', 'What it means for you', 'How sure'];

/** "What this means" as its labelled lines. The server writes three, one a
 *  line ("How sure: early..."); a paragraph written before that comes back as
 *  one unlabelled line. Pure. */
export function meaningLines(text: string | null | undefined): MeaningLine[] {
  return (text ?? '')
    .split('\n')
    .map(l => l.trim())
    .filter(Boolean)
    .map(l => {
      const label = MEANING_LABELS.find(m => l.startsWith(`${m}:`));
      return label ? { label, text: l.slice(label.length + 1).trim() } : { label: null, text: l };
    });
}

/** A name the lines may mention, and the item it opens. */
export interface NameLink {
  name: string;
  id: number;
}

/** The names a table's rows carry, for linking the lines to them: a row's
 *  `title` with its `id`, or a search player's `company` with its
 *  `company_id`. Pure. */
export function nameLinks(report: unknown): NameLink[] {
  const out = new Map<string, number>();
  const take = (rows: unknown) => {
    if (!Array.isArray(rows)) return;
    for (const r of rows) {
      if (!r || typeof r !== 'object') continue;
      const row = r as Record<string, unknown>;
      const name = typeof row.title === 'string' ? row.title : typeof row.company === 'string' ? row.company : null;
      const id = typeof row.id === 'number' ? row.id : typeof row.company_id === 'number' ? row.company_id : null;
      if (name && id && name.trim().length > 2 && !out.has(name)) out.set(name, id);
    }
  };
  const rep = (report ?? {}) as Record<string, unknown>;
  take(rep.rows);
  take(rep.players);
  return Array.from(out, ([name, id]) => ({ name, id }));
}

/** One line's text split into plain runs and linked names, longest names
 *  first so "Adit Pro" is not read as "Adit". Pure. */
export function linkRuns(text: string, links: NameLink[]): (string | NameLink)[] {
  const sorted = [...links].sort((a, b) => b.name.length - a.name.length);
  const runs: (string | NameLink)[] = [];
  let rest = text;
  while (rest) {
    let best: { at: number; link: NameLink } | null = null;
    for (const l of sorted) {
      const at = rest.indexOf(l.name);
      if (at < 0) continue;
      const before = at === 0 ? '' : rest[at - 1];
      const after = rest[at + l.name.length] ?? '';
      // Whole names only: "Vim" is not inside "Vimeo".
      if (/[A-Za-z0-9]/.test(before) || /[A-Za-z0-9]/.test(after)) continue;
      if (!best || at < best.at) best = { at, link: l };
    }
    if (!best) {
      runs.push(rest);
      break;
    }
    if (best.at) runs.push(rest.slice(0, best.at));
    runs.push(best.link);
    rest = rest.slice(best.at + best.link.name.length);
  }
  return runs;
}

export default function TableSummary({
  summary,
  onRewrite,
  links = [],
  onOpen,
}: {
  summary: BoardroomTableSummary;
  /** Names the lines may mention, from the table's rows (`nameLinks`). */
  links?: NameLink[];
  /** Open a named row's item. Without it the names are plain text. */
  onOpen?: (id: number) => void;
  /** Ask for it to be written again now. Offered only while it is out of date. */
  onRewrite?: () => Promise<{ writing: boolean }>;
}) {
  const [asked, setAsked] = useState(false);
  // A new paragraph arriving ends the wait.
  useEffect(() => setAsked(false), [summary.written_at]);
  const { text, stale } = summary;
  const writing = summary.writing || asked;
  // TOO LITTLE BEHIND THE TABLE: say what is missing, not a conclusion.
  if (summary.thin) {
    return (
      <section
        className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2 dark:border-gray-700 dark:bg-gray-800/60"
        data-testid="table-summary"
      >
        <div className="mb-1 text-[11px] uppercase tracking-wide text-gray-400">What this means</div>
        <p className="text-sm text-gray-600 dark:text-gray-300" data-testid="table-summary-thin">
          {summary.thin}
        </p>
      </section>
    );
  }
  // Nothing written and nothing on the way: say nothing rather than promise.
  if (!text && !writing) return null;
  return (
    <section
      className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2 dark:border-gray-700 dark:bg-gray-800/60"
      data-testid="table-summary"
    >
      <div className="mb-1 text-[11px] uppercase tracking-wide text-gray-400">What this means</div>
      {text ? (
        <div className="flex flex-col gap-0.5">
          {meaningLines(text).map((l, i) => (
            <p key={i} className="text-sm text-gray-800 dark:text-gray-100">
              {l.label && <span className="font-medium text-gray-500 dark:text-gray-400">{l.label}: </span>}
              {onOpen
                ? linkRuns(l.text, links).map((run, j) =>
                    typeof run === 'string' ? (
                      run
                    ) : (
                      <button
                        key={j}
                        type="button"
                        onClick={() => onOpen(run.id)}
                        className="text-indigo-700 underline decoration-dotted hover:decoration-solid dark:text-indigo-300"
                      >
                        {run.name}
                      </button>
                    ),
                  )
                : l.text}
            </p>
          ))}
        </div>
      ) : (
        <p className="text-sm italic text-gray-500 dark:text-gray-400">Writing a summary of this table…</p>
      )}
      {text && (
        <div className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-gray-500 dark:text-gray-400">
          {summary.written_at && <span>Written {writtenAgo(summary.written_at)}</span>}
          {writing ? (
            <span>· writing a new one…</span>
          ) : (
            stale && (
              <span className="text-amber-700 dark:text-amber-400" data-testid="table-summary-stale">
                · the numbers have changed since; a new one is written when the next run ends
              </span>
            )
          )}
          {!writing && stale && onRewrite && (
            <button
              type="button"
              onClick={() => {
                setAsked(true);
                onRewrite()
                  .then(r => setAsked(r.writing))
                  .catch(() => setAsked(false));
              }}
              className="text-indigo-600 hover:underline dark:text-indigo-300"
            >
              Write it again now
            </button>
          )}
        </div>
      )}
    </section>
  );
}
